<template>
  <div class="row mb-3">
    <div class="col">
      <button
        class="btn btn-primary me-3"
        type="button"
        @click="
          $router.push({
            name: 'DeviceEdit',
            params: { deviceId: 'new', customerId: filters.customer, facilityId: filters.facility },
          })
        "
      >
        Nova naprava
      </button>
      <button class="btn btn-outline-secondary" type="button" @click="exportDevices">Izvozi</button>
    </div>
  </div>

  <div class="row mb-3">
    <div class="col-sm-4">
      <label class="form-check-label" for="is-active">Prikaži aktivne</label>
    </div>
    <div class="col-sm-8">
      <div class="form-check form-switch">
        <input class="form-check-input" type="checkbox" id="is-active" v-model="filters.displayActive" />
      </div>
    </div>
  </div>
  <div class="row">
    <div class="col">
      <SelectCustomerEntity
        :customerID="filters.customer"
        :facilityID="filters.facility"
        :canChange="true"
        @change="
          (e) => {
            filters.customer = e.customerID
            filters.facility = e.facilityID
          }
        "
      />
    </div>
  </div>
  <SelectEnum class="row mb-3" v-model="filters.typeText" :types="deviceTypes" label="Tip naprave" />

  <div class="row">
    <div class="col">
      <button class="btn btn-outline-secondary btn-sm mb-3" @click="showBarcode = true" v-show="!showBarcode">
        Pokaži iskalnik s črtno kodo
      </button>
      <Barcode @detected="searchAsync" v-if="showBarcode" />
    </div>
  </div>

  <div class="row mt-5">
    <div class="col">
      <DeviceList :devices="filtered" :showType="true" :enableDepth="filters.typeText === 'all'" />
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth } from "../composition-api/useAuth"
import { listEntitiesAsync, searchDevicesAsync } from "../api"
import { EMPTY_GUID } from "../utils/constants"
import { useEnums } from "../composition-api/useEnums"
import DeviceList from "../components/DeviceList.vue"
import Barcode from "../components/Barcode.vue"
import exportFromJSON from "export-from-json"

defineOptions({ name: "Devices" })

const router = VueRouter.useRouter()
const { deviceTypes, loadEnumsAsync } = useEnums()
const showBarcode = ref(false)

const DEFAULT_DEVICE_FILTERS = () => {
  return {
    displayActive: true,
    facility: EMPTY_GUID,
    customer: EMPTY_GUID,
    typeText: "all",
  }
}

const resetFilters = async () => Object.assign(filters, DEFAULT_DEVICE_FILTERS())
const filters = reactive(DEFAULT_DEVICE_FILTERS())

const { fetchAuthAsync } = useAuth()

const devices = ref([])

onMounted(async () => {
  await loadEnumsAsync()
  devices.value = await listEntitiesAsync(fetchAuthAsync, "Devices", "?variant=Full")
})

const selectDevice = (deviceId, openInNewWindow = false) => {
  let routeData = router.resolve({ name: "DeviceEdit", params: { deviceId } })
  if (openInNewWindow) {
    window.open(routeData.href, "_blank")
  } else {
    router.push(routeData)
  }
}

const searchAsync = async (bc) => {
  const tempDevices = await searchDevicesAsync(fetchAuthAsync, bc?.ident)
  if (tempDevices.length === 1) {
    router.push({ name: "DeviceEdit", params: { deviceId: tempDevices[0].id } })
  }
}

const exportDevices = () => {
  const exportType = exportFromJSON.types["xls"]
  const data = devices.value.map((d) => {
    return {
      tip: d.typeText,
      objekt: d.facilityName,
      stNaprave: d.deviceNumber,
      dobavitelj: d.supplier,
      nalepka: d.label,
      vGaranciji: d.isWarranty ? "Da" : "Ne",
      aktivna: d.isActive ? "Da" : "Ne",
      zadnjiCO2: d.lastCO2Sensor != "0001-01-01" ? d.lastCO2Sensor : "",
      zadnjeCiscenjeKondenzatorjaMokro: d.lastCondenserCleaning != "0001-01-01" ? d.lastCondenserCleaning : "",
      zadnjeCiscenjeKondenzatorjaSuho: d.lastCondenserCleaningDry != "0001-01-01" ? d.lastCondenserCleaningDry : "",
      zadnjiTestPuscanja: d.lastLeakageTest != "0001-01-01" ? d.lastLeakageTest : "",
      zadnjiServis: d.lastService != "0001-01-01" ? d.lastService : "",
      zadnjaKalibracijaTermometra: d.lastThermometerCalibration != "0001-01-01" ? d.lastThermometerCalibration : "",
    }
  })
  exportFromJSON({ data, fileName: "devices.xls", exportType })
}

const filtered = computed(() => {
  let result = devices.value.filter((w) => w.isActive === filters.displayActive)
  if (filters.typeText !== "all") {
    result = result.filter((w) => w.typeText === filters.typeText)
  }
  if (filters.facility !== EMPTY_GUID) {
    result = result.filter((f) => f.facilityID === filters.facility)
  }
  if (filters.customer !== EMPTY_GUID) {
    result = result.filter((f) => f.customerID === filters.customer)
  }
  return result
})
</script>
